/**
 * VersionComparePanel — side-by-side evaluation comparison for agent template versions.
 * Demo page: /tables/basic-tables (compact table inside a card).
 *
 * Flow:
 *   1. User checks ≥2 versions + sets sample count → clicks Compare.
 *   2. POST /compare → AgentTemplateVersionCompareResult
 *      - status === 'no_dataset' → informational message; no polling.
 *      - status === 'queued'     → compareJobId set; poll GET /compare/:id until complete.
 *   3. While polling: show progress bar.  When complete: show ranked table.
 *
 * Advisory note: results are informational and do not gate promotion.
 * Gated by <Can policy="CanManageAgents">.
 */
import { useState } from 'react';
import { BarChart2 } from 'lucide-react';
import { Badge } from '../ui/Badge.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { DataTable, type DataTableColumn } from '../ui/DataTable.js';
import { sentenceCase } from '../ui/FilterSelect.js';
import { Input } from '../ui/Input.js';
import { Spinner } from '../ui/Spinner.js';
import { Checkbox } from '../tailgrids/core/checkbox.js';
import { Can } from '../auth/index.js';
import { useCompareVersions, useVersionComparison } from '../../hooks/useVersionCompare.js';
import { STATUS_VARIANT } from './VersionCard.js';
import type { AgentTemplateVersion } from '../../services/api/template-version.api.js';
import type { AgentTemplateVersionComparison } from '../../generated/api/types.gen.js';

interface VersionComparePanelProps {
  templateId: string;
  versions: AgentTemplateVersion[];
}

export function VersionComparePanel({ templateId, versions }: VersionComparePanelProps) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [samples, setSamples] = useState(5);

  const compareMutation = useCompareVersions(templateId);
  const mutData = compareMutation.data;

  // Derive job state from mutation response (stateless — no internal useState).
  const isNoDataset = mutData?.status === 'no_dataset';
  const compareJobId: string | null =
    mutData?.status === 'queued' && mutData.compareJobId ? mutData.compareJobId : null;
  const jobVersionIds: number[] = mutData?.versionIds ?? [];
  const jobSamples: number | undefined = mutData?.samples;

  const comparisonQuery = useVersionComparison(templateId, compareJobId, jobVersionIds, jobSamples);
  const comparison = comparisonQuery.data;

  const canCompare = selectedIds.size >= 2;

  const handleCompare = () => {
    compareMutation.mutate({ versionIds: Array.from(selectedIds), samples });
  };

  const toggleVersion = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  return (
    <Can policy="CanManageAgents">
      <Card title="Compare versions" className="space-y-4">
        {/* Version checkboxes */}
        {versions.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs text-muted">Select 2 or more versions to compare:</p>
            <div className="flex flex-wrap gap-3">
              {versions.map((v) => (
                <Checkbox
                  key={v.id}
                  isSelected={selectedIds.has(v.id)}
                  onChange={(checked) => toggleVersion(v.id, checked)}
                >
                  <span className="text-xs font-medium text-body">v{v.version}</span>
                  <Badge variant={STATUS_VARIANT[v.status] ?? 'neutral'} size="sm">
                    {sentenceCase(v.status)}
                  </Badge>
                </Checkbox>
              ))}
            </div>
          </div>
        )}

        {/* Samples + Compare button */}
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-muted">
            Samples:
            <Input
              type="number"
              min={1}
              max={10}
              value={samples}
              onChange={(e) => setSamples(Math.max(1, Math.min(10, Number(e.target.value))))}
              className="w-20 px-2 py-1 text-xs"
            />
          </label>
          <Button
            size="sm"
            onClick={handleCompare}
            disabled={!canCompare || compareMutation.isPending}
            loading={compareMutation.isPending}
          >
            <BarChart2 size={14} aria-hidden="true" />
            Compare
          </Button>
        </div>

        {/* No dataset notice */}
        {isNoDataset && (
          <div className="bg-surface-alt border rounded-lg px-3 py-2 text-xs text-muted" role="status">
            This template has no golden evaluation dataset — comparison needs one first.
          </div>
        )}

        {/* In-progress progress */}
        {compareJobId && comparison && !comparison.complete && (
          <div className="flex items-center gap-2 text-xs text-muted" role="status">
            <Spinner label="Running comparison" />
            Running: {comparison.completed} / {comparison.expected} evaluations complete
          </div>
        )}

        {/* Initial queued state before first poll result */}
        {compareJobId && !comparison && !comparisonQuery.isError && (
          <div className="flex items-center gap-2 text-xs text-muted" role="status">
            <Spinner label="Starting comparison" />
            Starting comparison…
          </div>
        )}

        {/* Complete: ranked results table */}
        {comparison?.complete && <ComparisonResultsTable versions={versions} comparison={comparison} />}

        {/* Mutation error */}
        {compareMutation.isError && (
          <p className="text-danger text-xs">Failed to start comparison. Please try again.</p>
        )}
      </Card>
    </Can>
  );
}

// ---------- Results sub-component ----------

interface ComparisonResultsTableProps {
  versions: AgentTemplateVersion[];
  comparison: AgentTemplateVersionComparison;
}

function ComparisonResultsTable({ versions, comparison }: ComparisonResultsTableProps) {
  const ranked = [...comparison.versions].sort((a, b) => a.rank - b.rank);
  type Row = (typeof ranked)[number];

  const columns: DataTableColumn<Row>[] = [
    {
      key: 'rank',
      header: 'Rank',
      render: (row) => <span className="font-semibold text-heading">{row.rank === 1 ? '1 ★' : row.rank}</span>,
    },
    {
      key: 'version',
      header: 'Version',
      render: (row) => {
        const ver = versions.find((v) => v.id === row.versionId);
        return <span className="font-medium text-heading">{ver ? `v${ver.version}` : 'Unknown version'}</span>;
      },
    },
    {
      key: 'avgScore',
      header: 'Average score',
      render: (row) => (
        <span className={row.rank === 1 ? 'font-semibold text-success' : 'text-body'}>{row.avgScore.toFixed(2)}</span>
      ),
    },
    {
      key: 'delta',
      header: 'Delta',
      render: (row) =>
        row.deltaFromBest === 0 ? (
          <span className="text-muted">—</span>
        ) : (
          <span className="text-danger">{row.deltaFromBest.toFixed(2)}</span>
        ),
    },
    { key: 'samples', header: 'Samples', render: (row) => <span className="text-muted">{row.sampleCount}</span> },
    {
      key: 'verdicts',
      header: 'Verdicts',
      render: (row) => (
        <span className="text-muted">
          {Object.entries(row.verdicts)
            .map(([verdict, count]) => `${sentenceCase(verdict)}: ${count}`)
            .join(', ')}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted">Results are informational and do not gate promotion.</p>
      <DataTable
        columns={columns}
        rows={ranked}
        rowKey={(row) => row.versionId}
        aria-label="Version comparison results"
      />
    </div>
  );
}
